/**
 * Unified client initialization for workers and frontends
 * Consolidates scattered initialization patterns to prevent configuration bugs
 */

import { newWorkersChannelClient, useSandboxWrappers } from 'workers-channel-js'
import { newNamespaceClient as createNamespaceClient } from 'workers-namespace-js'
import { wrappers } from './index.js'

// Service URL constants
export const CHANNEL_SERVICE_URL = 'https://channel.monoqueue.com'
export const CHANNEL_WEBSOCKET_URL = 'wss://channel.monoqueue.com/ws'
export const NAMESPACE_SERVICE_URL = 'https://namespace.monoqueue.com'

/**
 * Create a worker client with standard configuration
 *
 * @param {Object} options
 * @param {string} options.privateKey - Worker's Ed25519 private key (64-char hex)
 * @param {Object} options.sandbox - RPC method implementations
 * @param {Array} options.permissions - Permission control rules for SimplePermissionControl
 * @param {number} [options.callTimeout=30000] - RPC call timeout in milliseconds
 * @param {boolean} [options.enableNamespace=true] - Enable namespace client for callByName
 * @param {string} [options.callTransport='ws'] - Transport mode: 'ws', 'http', or 'auto'
 * @param {boolean} [options.applyMiddleware=true] - Auto-wrap sandbox with requestLog + SimplePermissionControl
 * @param {string} [options.serviceUrl] - Override channel service URL
 * @param {string} [options.websocketUrl] - Override WebSocket URL
 * @param {string} [options.namespaceUrl] - Override namespace service URL
 * @returns {Object} Workers-channel client instance
 */
export const createWorkerClient = ({
  privateKey,
  sandbox,
  permissions,
  callTimeout = 30000,
  enableNamespace = true,
  callTransport = 'ws',
  applyMiddleware = true,
  serviceUrl = CHANNEL_SERVICE_URL,
  websocketUrl = CHANNEL_WEBSOCKET_URL,
  namespaceUrl = NAMESPACE_SERVICE_URL,
}) => {
  // Validate required params
  if (!privateKey) {
    throw new Error('[createWorkerClient] privateKey is required')
  }
  if (!sandbox) {
    throw new Error('[createWorkerClient] sandbox is required')
  }
  if (!permissions && applyMiddleware) {
    throw new Error('[createWorkerClient] permissions required when applyMiddleware=true')
  }

  // Create namespace client if enabled
  let namespaceClient = null
  if (enableNamespace) {
    namespaceClient = createNamespaceClient({
      namespaceUrl,
      selector: 'random',
    })
  }

  // Apply middleware if enabled
  let finalSandbox = sandbox
  if (applyMiddleware) {
    finalSandbox = useSandboxWrappers(sandbox, [
      wrappers.requestLog,
      wrappers.SimplePermissionControl(permissions, { getNamespaceClient: () => namespaceClient }),
    ])
  }

  // Create workers-channel client
  const client = newWorkersChannelClient({
    serviceUrl,
    websocketUrl,
    privateKey,
    sandbox: finalSandbox,
    callTransport,
    callTimeout,
    namespaceClient,
  })

  return client
}

/**
 * Create a frontend client with standard configuration
 *
 * @param {Object} options
 * @param {Function} options.getPrivateKey - Function that returns hex-encoded private key
 * @param {Function} options.getAccessToken - Async function that returns JWT token (or null)
 * @param {Object} [options.sandbox] - Optional local sandbox methods (for local-first features like session storage)
 * @param {string} [options.serviceUrl] - Override channel service URL
 * @param {string} [options.namespaceUrl] - Override namespace service URL
 * @param {number} [options.callTimeout] - Override default call timeout
 * @returns {Object} Workers-channel client instance with JWT auto-injection
 */
export const createFrontendClient = ({
  getPrivateKey,
  getAccessToken,
  sandbox,
  serviceUrl = CHANNEL_SERVICE_URL,
  namespaceUrl = NAMESPACE_SERVICE_URL,
  callTimeout,
}) => {
  // Validate required params
  if (!getPrivateKey) {
    throw new Error('[createFrontendClient] getPrivateKey is required')
  }
  if (!getAccessToken) {
    throw new Error('[createFrontendClient] getAccessToken is required')
  }

  // Get private key
  const privateKey = getPrivateKey()
  if (!privateKey) {
    throw new Error('[createFrontendClient] getPrivateKey() returned null/undefined')
  }

  // Create namespace client
  const namespaceClient = createNamespaceClient({
    namespaceUrl,
    selector: 'random',
  })

  // Create workers-channel client (HTTP transport, no WebSocket)
  // sandbox enables local calls via callByName('local', ...) without network
  const clientConfig = {
    serviceUrl,
    privateKey,
    callTransport: 'http',
    namespaceClient,
    ...(sandbox && { sandbox }),
  }

  if (callTimeout) {
    clientConfig.callTimeout = callTimeout
  }

  const client = newWorkersChannelClient(clientConfig)

  // Add JWT-authenticated call methods (keep original call/callByName unchanged)
  client.callWithAuth = async (worker, method, params, options = {}) => {
    const accessToken = await getAccessToken()
    if (accessToken) {
      options.headers = {
        ...options.headers,
        authorization: `Bearer ${accessToken}`,
      }
    }
    return client.call(worker, method, params, options)
  }

  // Add JWT-authenticated callByName method
  if (client.callByName) {
    client.callByNameWithAuth = async (workerName, method, params, options = {}) => {
      const accessToken = await getAccessToken()
      if (accessToken) {
        options.headers = {
          ...options.headers,
          authorization: `Bearer ${accessToken}`,
        }
      }
      return client.callByName(workerName, method, params, options)
    }
  }

  return client
}

/**
 * Generate ephemeral Ed25519 private key
 * Utility function for platforms that don't need to persist keys
 * @returns {string} Hex-encoded private key (64 chars)
 */
export const generateEphemeralPrivateKey = () => {
  const crypto = globalThis.crypto || (typeof window !== 'undefined' && window.crypto)
  if (!crypto?.getRandomValues) {
    throw new Error('[generateEphemeralPrivateKey] Web Crypto API not available')
  }

  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
